import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, BookOpenCheck, Receipt, ListChecks, FileSpreadsheet, Scale } from "lucide-react";
import { PortalPage, Spinner, ErrorNote } from "@/components/PortalPage";
import { api } from "@/lib/api";
import { BkTabs, EntitySwitcher, useBkEntities } from "@/components/bookkeeping/BkShell";

const STATS = [
  ["bk_bank_accounts", "Bank accounts", Landmark],
  ["bk_bank_transactions", "Bank transactions", FileSpreadsheet],
  ["bk_expected_transactions", "Expected payments", ListChecks],
  ["bk_receipts", "Receipts", Receipt],
  ["bk_rules", "Rules", BookOpenCheck],
  ["bk_reconciliations", "Month-end closes", Scale],
];

const Field = ({ label, testId, ...p }) => (
  <label className="flex flex-col gap-1 text-xs font-bold text-neutral-400">
    {label}
    <input data-testid={testId} className="rounded-lg border border-[#333] bg-[#0F0F0F] px-3 py-2 text-sm font-medium text-white outline-none focus:border-[#4CCC06]" {...p} />
  </label>
);

const EntityForm = ({ entity, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => setF({ legal_name: entity.legal_name || "", ein_last4: entity.ein_last4 || "", fiscal_year_start: entity.fiscal_year_start || "", monthly_target: entity.monthly_target ?? "" }), [entity]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api(`/bookkeeping/entities/${entity.id}`, { method: "PATCH", body: { legal_name: f.legal_name, ein_last4: f.ein_last4 || null, fiscal_year_start: f.fiscal_year_start || null, monthly_target: f.monthly_target === "" ? null : Number(f.monthly_target) } });
      toast.success("Entity saved"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-[#333] bg-[#141414] p-5" data-testid="bk-entity-form">
      <p className="mb-4 text-[11px] font-bold tracking-[3px] text-[#4CCC06]">ENTITY DETAILS</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Legal name" testId="bk-legal-name" value={f.legal_name || ""} onChange={set("legal_name")} />
        <Field label="EIN (last 4)" testId="bk-ein-last4" maxLength={4} inputMode="numeric" placeholder="Not set" value={f.ein_last4 || ""} onChange={set("ein_last4")} />
        <Field label="Fiscal year starts (MM-DD)" testId="bk-fiscal-start" placeholder="Not set, e.g. 01-01" value={f.fiscal_year_start || ""} onChange={set("fiscal_year_start")} />
        <Field label="Monthly profit target (USD)" testId="bk-monthly-target" type="number" placeholder="Not set" value={f.monthly_target} onChange={set("monthly_target")} />
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-neutral-400" data-testid="bk-qbo-file-note">
          {entity.has_qbo_file ? "Has its own QuickBooks Online file. Connect it in Phase 1." : "No QuickBooks file yet. Books are kept in the portal only for now."}
        </p>
        <button onClick={save} disabled={busy} data-testid="bk-entity-save" className="rounded-lg bg-[#4CCC06] px-4 py-2 text-xs font-extrabold text-black disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
      </div>
    </section>
  );
};

export default function BookkeepingHome() {
  const { entities, error, entityId, setEntityId, reload, entity } = useBkEntities();
  const [ov, setOv] = useState(null);
  useEffect(() => { setOv(null); api(`/bookkeeping/overview?entity_id=${entityId}`).then(setOv).catch(() => setOv({ counts: {} })); }, [entityId]);
  return (
    <PortalPage eyebrow="BOOKKEEPING" title={entity ? entity.name : "Bookkeeping"} testId="bookkeeping-home" width="max-w-5xl">
      {error && <ErrorNote>{error}</ErrorNote>}
      {!entities ? <Spinner /> : (
        <>
          <EntitySwitcher entities={entities} entityId={entityId} onChange={setEntityId} />
          <BkTabs />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="bk-overview-stats">
            {STATS.map(([k, label, Icon]) => (
              <div key={k} className="rounded-xl border border-[#262626] bg-[#141414] p-4" data-testid={`bk-stat-${k}`}>
                <Icon size={16} className="text-[#4CCC06]" />
                <p className="mt-2 text-2xl font-extrabold text-white">{ov ? ov.counts[k] ?? 0 : "–"}</p>
                <p className="text-xs text-neutral-400">{label}</p>
              </div>
            ))}
          </div>
          <p className="rounded-xl border border-dashed border-[#333] p-4 text-sm text-neutral-400" data-testid="bk-phase-note">
            Phase 0 is set up: three entities and the record-to-entity mapping. Bank feeds, QuickBooks connections, matching and reports arrive in the next phases.
          </p>
          {entity && <EntityForm entity={entity} onSaved={reload} />}
        </>
      )}
    </PortalPage>
  );
}
